import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class AllocationDenialService {
  private baseUrl = environment.baseUrl + 'integration';
  // private baseUrl = 'http://localhost:8000/integration';

  constructor(private http: HttpClient) {}

  // Method to mark an invoice as having allocation denial
  markAsAllocationDenial(invoiceId: string) {
    try {
      const denials = this.getRecentAllocationDenials();
      const newDenial = {
        // Store invoiceId as string consistently
        invoiceId: String(invoiceId),
        timestamp: new Date().toISOString()
      };
      const existingDenials = localStorage.getItem('recentAllocationDenials');
      const allDenials = existingDenials ? JSON.parse(existingDenials) : [];
      allDenials.push(newDenial);
      // Keep only last 50 entries and within last hour
      const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000);
      const filteredDenials = allDenials
        .filter((denial: any) => new Date(denial.timestamp) > oneHourAgo)
        .slice(-50);
      
      localStorage.setItem('recentAllocationDenials', JSON.stringify(filteredDenials));
      try {
        // Also store the last created invoice id so other parts of the app (All Documents)
        // can open it automatically even if the router navigation didn't include query params.
        localStorage.setItem('lastCreatedInvoiceToOpen', String(invoiceId));
        // Also set a transient global suppression flag so any immediate UI alerts
        // triggered by the allocation-denial flow (navigation, dialogs, toasts)
        // are ignored. This complements the interceptor flag for 405 responses.
        try {
          (window as any).__suppressInvoice405Swal = true;
          // Keep this a bit longer because we may navigate and render other components
          setTimeout(() => { (window as any).__suppressInvoice405Swal = false; }, 3000);
          } catch (e) {
          // ignore
        }
      } catch (err) {
        console.error('🔴 [AllocationDenialService] Failed to store lastCreatedInvoiceToOpen:', err);
      }
    } catch (error) {
      console.error('🔴 [AllocationDenialService] Error saving allocation denial to localStorage:', error);
    }
  }

  // Helper method to get recent allocation denial invoices from localStorage
  getRecentAllocationDenials(): string[] {
    try {
      const denials = localStorage.getItem('recentAllocationDenials');
      if (denials) {
        const parsedDenials = JSON.parse(denials);
        // Filter out denials older than 1 hour
        const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000);
        const filteredDenials = parsedDenials.filter((denial: any) => {
          const denialTime = new Date(denial.timestamp);
          const isRecent = denialTime > oneHourAgo;
          return isRecent;
        });
        
          // Return invoice IDs as strings for consistent comparisons
          const invoiceIds = filteredDenials.map((denial: any) => String(denial.invoiceId));
        return invoiceIds;
      }
    } catch (error) {
      console.error('🟡 [AllocationDenialService] Error reading allocation denials from localStorage:', error);
    }
    
    return [];
  }

  // Method to check if an invoice has allocation denial
  hasAllocationDenial(invoiceId: string): boolean {
    const recentDenials = this.getRecentAllocationDenials();
    // Normalize comparison to strings to avoid number/string mismatches
    const key = String(invoiceId);
    const hasAllocation = recentDenials.map(String).includes(key);
    
    return hasAllocation;
  }

  // Method to clear allocation denial marking for an invoice
  clearAllocationDenial(invoiceId: string) {
    try {
      const existingDenials = localStorage.getItem('recentAllocationDenials');
      if (existingDenials) {
        const allDenials = JSON.parse(existingDenials);
        const filteredDenials = allDenials.filter((denial: any) => String(denial.invoiceId) !== String(invoiceId));
        localStorage.setItem('recentAllocationDenials', JSON.stringify(filteredDenials));
      }
    } catch (error) {
      console.error('Error clearing allocation denial from localStorage:', error);
    }
  }

  // API Methods for handling allocation denial options

  /**
   * Cancel allocation request
   * API: http://localhost:8000/integration/InvoiceDecisionApiCancel/{invoiceId}
   */
  cancelAllocationRequest(invoiceId: string): Observable<any> {
    const url = `${this.baseUrl}/InvoiceDecisionApiCancel/${invoiceId}`;
    return this.http.get(url);
  }

  /**
   * Continue without allocation number
   * API: http://localhost:8000/integration/InvoiceDecisionApiContinue/{invoiceId}
   */
  continueWithoutAllocation(invoiceId: string): Observable<any> {
    const url = `${this.baseUrl}/InvoiceDecisionApiContinue/${invoiceId}`;
    return this.http.get(url);
  }

  /**
   * Reverse charge - customer pays tax
   * API: http://localhost:8000/integration/InvoiceReversingTheCharge/{invoiceId}
   */
  reverseCharge(invoiceId: string): Observable<any> {
    const url = `${this.baseUrl}/InvoiceReversingTheCharge/${invoiceId}`;
    return this.http.get(url);
  }

  /**
   * Transfer to specialized unit for further objection
   * API: http://localhost:8000/integration/InvoiceDecisionApiFurtherObjection/{invoiceId}
   */
  transferToSpecializedUnit(invoiceId: string): Observable<any> {
    const url = `${this.baseUrl}/InvoiceDecisionApiFurtherObjection/${invoiceId}`;
    return this.http.get(url);
  }
}